/**
 * Recently viewed products.
 *
 * The history is a list of product handles in the visitor's own localStorage.
 * Nothing is sent anywhere and no app is involved; Liquid cannot know the list,
 * which is why the row is built here.
 *
 * The cards are NOT assembled in JavaScript. Each one is fetched from Shopify's
 * own rendering of sections/alpha-recent-card.liquid through the Section
 * Rendering API, the same trick cart-recommendations.js uses, so the cards in
 * this row are the same Liquid, the same money formatting and the same classes
 * as every other product card on the site.
 *
 * Failure is always silent and always ends with the row hidden. A heading over
 * an empty strip is worse than no row at all - it reads as a broken page.
 */

const ALPHA_RECENT_KEY = 'alpha:recently-viewed-handles';
const ALPHA_RECENT_MAX = 12;
// Shopify handles are lowercase alphanumerics with hyphens. Anything else in
// storage was not put there by this file, and it is not going into a URL.
const ALPHA_RECENT_HANDLE = /^[a-z0-9][a-z0-9_-]*$/;

function alphaRecentRead() {
  try {
    const raw = localStorage.getItem(ALPHA_RECENT_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((handle) => typeof handle === 'string' && ALPHA_RECENT_HANDLE.test(handle));
  } catch (error) {
    // Private browsing, a full quota or a corrupt value. Degrade to "no
    // history" rather than taking the rest of the page down with it.
    return [];
  }
}

function alphaRecentRecord(handle) {
  if (!handle || !ALPHA_RECENT_HANDLE.test(handle)) return;

  try {
    const history = alphaRecentRead().filter((entry) => entry !== handle);
    history.unshift(handle);
    localStorage.setItem(ALPHA_RECENT_KEY, JSON.stringify(history.slice(0, ALPHA_RECENT_MAX)));
  } catch (error) {
    /* storage unavailable - the row simply stays empty on the next product */
  }
}

if (!customElements.get('alpha-recently-viewed')) {
  customElements.define(
    'alpha-recently-viewed',
    class AlphaRecentlyViewed extends HTMLElement {
      connectedCallback() {
        const handle = this.dataset.handle || '';

        // Read BEFORE recording, and drop the current product either way: this
        // page is not something the shopper needs a link back to.
        const history = alphaRecentRead().filter((entry) => entry !== handle);
        alphaRecentRecord(handle);

        const limit = parseInt(this.dataset.limit, 10) || 4;
        // Ask for a couple more than are wanted. Products get unpublished, and
        // a history of five with one 404 in it should still fill the row rather
        // than leaving a short one.
        const handles = history.slice(0, limit + 2);
        if (!handles.length) return;

        this.render(handles, limit);
      }

      async render(handles, limit) {
        const target = this.querySelector('[data-alpha-recent-target]');
        if (!target) return;

        // Requested together, appended in history order. Fetching them in
        // sequence would make a four-card row take four round trips.
        const cards = await Promise.all(handles.map((handle) => this.fetchCard(handle)));
        const found = cards.filter(Boolean).slice(0, limit);
        if (!found.length) return;

        // textContent rather than innerHTML to empty it: same result, but it
        // never hands a string back to the HTML parser. The cards themselves are
        // nodes parsed out of the shop's own section response, not markup built
        // from a string here.
        target.textContent = '';
        found.forEach((card) => target.appendChild(card));
        this.hidden = false;
      }

      async fetchCard(handle) {
        const root = (this.dataset.root || '/').replace(/\/$/, '');
        const sectionId = this.dataset.sectionId || 'alpha-recent-card';
        const url = `${root}/products/${encodeURIComponent(handle)}?section_id=${encodeURIComponent(
          sectionId
        )}`;

        try {
          const response = await fetch(url);
          // A 404 is the ordinary case, not an error: the shopper looked at
          // something the merchant has since unpublished. Skip that card and
          // keep the rest of the row.
          if (!response.ok) return null;

          const text = await response.text();
          const card = new DOMParser()
            .parseFromString(text, 'text/html')
            .querySelector('[data-alpha-recent-card]');

          return card || null;
        } catch (error) {
          return null;
        }
      }
    }
  );
}
